/**
 * Lo de ba mien - man hinh nguoi choi.
 *
 * Bon diem nghiep vu quyet dinh cach bay man hinh nay:
 *
 *   1. `points_x2` la so NUA DIEM (10 = 5 diem). Neu hien thang con so may chu dung
 *      thi nguoi choi doc ra gap doi so diem that va dat nham muc cuoc. O day nguoi
 *      choi luon nhap va nhin thay SO DIEM; nua diem chi ton tai o duong truyen.
 *
 *   2. Ty le CHOT theo moc mo ky, khong phai luc mua (LDE-R04). Nen bang ty le hien
 *      o day la ty le se ap, va con so "tien cuoc"/"co the thang" tinh ngay tren do -
 *      khong duoc de nguoi choi doan.
 *
 *   3. Dang nhap gop tach thanh nhieu dong, moi dong TINH TIEN RIENG. `DAN_DE` 20 so
 *      la 20 lan tien cuoc, `BAO_LO` 3 dai la 3 lan. Man hinh phai noi ro so dong va
 *      tong tien truoc khi bam, vi day la cho de hieu nham nhat cua san pham.
 *
 *   4. Khoa bat bien sinh MOT LAN cho moi lan bam mua va giu nguyen khi thu lai
 *      (ID-01). Sinh lai khoa moi lan thu la tu bo lop chong tru tien hai lan.
 */

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  lodeApi,
  type LodeLineInput,
  type LodeRegion,
  type LodeStation,
} from '../lib/lode-api';
import {
  LODE_BET_TYPES,
  BAO_LO_MAX_DRAWS,
  BAO_LO_MIN_DRAWS,
  betTypesForRegion,
} from '../lib/lode-bet-types';
import {
  CONFIRM_WINDOW_MS,
  calcSlip,
  expandRows,
  newRow,
  type SlipRow,
} from '../lib/lode-slip';
import { ApiError } from '../lib/api';
import { formatMoney } from '../lib/money';
import { useSession } from '../lib/session';
import { Badge, Button, Card, EmptyState, ErrorList, Skeleton } from '../components/ui';
import styles from './LoDe.module.css';

const REGIONS: { code: LodeRegion; label: string }[] = [
  { code: 'MB', label: 'Miền Bắc' },
  { code: 'MT', label: 'Miền Trung' },
  { code: 'MN', label: 'Miền Nam' },
];

const TICKET_STATUS: Readonly<Record<string, { label: string; tone: 'neutral' | 'positive' | 'warning' }>> = {
  ACTIVE: { label: 'Đang chờ', tone: 'warning' },
  SETTLED: { label: 'Đã quyết toán', tone: 'neutral' },
  REFUNDED: { label: 'Đã hoàn tiền', tone: 'positive' },
};

const LINE_STATUS: Readonly<Record<string, { label: string; tone: 'neutral' | 'positive' | 'negative' | 'warning' }>> = {
  ACTIVE: { label: 'Chờ kết quả', tone: 'warning' },
  WON: { label: 'Trúng', tone: 'positive' },
  LOST: { label: 'Trượt', tone: 'neutral' },
  REFUNDED: { label: 'Hoàn tiền', tone: 'positive' },
};

/** Gioi han cua may chu: 50 dong nhap, 200 dong tinh tien sau khi tach dang gop. */
const MAX_INPUT_LINES = 50;
const MAX_ATOMIC_LINES = 200;

/**
 * Nhan giờ chốt — trả về CẢ CÂU, không chỉ phần đếm ngược.
 *
 * Trước đây hàm này chỉ trả phần thời gian và nơi gọi ghép thêm tiền tố "Chốt sau",
 * nên khi kỳ đã đóng thì màn hình hiện đúng chữ "Chốt sau đã chốt". Ghép chuỗi ở nơi
 * gọi luôn hỏng theo kiểu đó: nơi gọi không biết hàm sắp trả về một mốc hay một trạng
 * thái. Trả trọn câu thì không còn chỗ để ghép sai.
 */
function nhanGioChot(iso: string, now: number): string {
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return 'Đã chốt';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `Chốt sau ${h} giờ ${m} phút`;
  if (m > 0) return `Chốt sau ${m} phút ${s} giây`;
  return `Chốt sau ${s} giây`;
}

/** Gio:phut theo gio Viet Nam - lich dai la gio Viet Nam, khong theo mui gio cua may. */
function gioVn(iso: string): string {
  return new Date(iso).toLocaleTimeString('vi-VN', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Ho_Chi_Minh',
  });
}

/** Nhan cho ky da xep hang nhung chua toi gio mo ban. */
function nhanGioMo(iso: string, now: number): string {
  const giay = Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 1000));
  if (giay === 0) return 'Đang mở…';
  if (giay < 60) return `Mở sau ${giay} giây`;
  return `Mở sau ${Math.floor(giay / 60)} phút ${giay % 60} giây`;
}

/** Dong ho dem nguoc cua buoc xac nhan, dang phut:giay. */
function nhanConLai(expiresAt: number, now: number): string {
  const giay = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  return `${Math.floor(giay / 60)}:${String(giay % 60).padStart(2, '0')}`;
}

export function LoDePage(): JSX.Element {
  const { isAuthenticated } = useSession();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<'dat-cuoc' | 've-cua-toi'>('dat-cuoc');
  const [region, setRegion] = useState<LodeRegion>('MB');
  const [betType, setBetType] = useState<string>('DE');
  const [rows, setRows] = useState<SlipRow[]>(() => [newRow()]);
  const [selectedDraws, setSelectedDraws] = useState<string[]>([]);
  const [lastBought, setLastBought] = useState<{ lines: number; stake: string } | null>(null);
  /**
   * Phieu dang cho xac nhan (khach yeu cau 08/10/2026: danh xong co hai phut de xem lai,
   * khong thich thi doi so, doi tien). Chua tru tien cho toi khi bam xac nhan - ve da
   * mua thi KHONG huy duoc (LOT-R20), nen buoc xem lai phai dung TRUOC khi mua.
   *
   * Chup lai cac dong luc bam: nguoi choi dang xem dung thu se gui di, ke ca khi bang
   * ty le doc lai trong luc cho. ID-01: khoa bat bien sinh moi lan mo buoc xac nhan va
   * giu nguyen khi thu lai trong cung buoc do - sua phieu la mot lan mua khac.
   */
  const [confirm, setConfirm] = useState<{
    lines: LodeLineInput[];
    rows: { key: string; label: string; points: string; stake: string; payout: string }[];
    drawNames: string[];
    betLabel: string;
    atomicLines: number;
    stake: string;
    expiresAt: number;
    idempotencyKey: string;
  } | null>(null);
  const [confirmExpired, setConfirmExpired] = useState(false);
  const [openTicket, setOpenTicket] = useState<string | null>(null);
  const [verifyDraw, setVerifyDraw] = useState<string | null>(null);

  /**
   * Đồng hồ nhịp một giây, chỉ để đếm ngược chạy thật.
   *
   * Không có nó thì nhãn "Chốt sau 45 giây" đứng im cho tới lần đọc lại dữ liệu kế
   * tiếp, và người xem tưởng trang bị treo. Kỳ siêu tốc có chu kỳ tính bằng phút nên
   * đây là chi tiết người chơi nhìn vào nhiều nhất.
   */
  const [gioHienTai, setGioHienTai] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setGioHienTai(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const stations = useQuery({
    queryKey: ['lode', 'stations'],
    queryFn: () => lodeApi.stations(),
    refetchInterval: 20_000,
  });
  const fastDraws = useQuery({
    queryKey: ['lode', 'fast-draws'],
    queryFn: () => lodeApi.fastDraws(),
    // Chu ky sieu toc ngan nhat la mot phut: doc lai thua thi man hinh moi kip ky moi.
    refetchInterval: 5_000,
  });
  /** Ma dai cua cac ky dang chon - de hoi dung ty le cua tung dai. */
  const selectedStationCodes = useMemo(() => {
    const byDraw = new Map<string, string>();
    for (const st of stations.data?.stations ?? []) {
      if (st.open_draw) byDraw.set(st.open_draw.id, st.code);
    }
    return [...new Set(selectedDraws.map((id) => byDraw.get(id)).filter((c): c is string => !!c))].sort();
  }, [stations.data, selectedDraws]);

  const odds = useQuery({
    queryKey: ['lode', 'odds', region, selectedStationCodes.join(',')],
    queryFn: () => lodeApi.odds(region, selectedStationCodes),
  });
  const tickets = useQuery({
    queryKey: ['lode', 'tickets'],
    queryFn: () => lodeApi.tickets(),
    enabled: tab === 've-cua-toi',
    refetchInterval: 15_000,
  });
  const ticketDetail = useQuery({
    queryKey: ['lode', 'ticket', openTicket],
    queryFn: () => lodeApi.ticket(openTicket!),
    enabled: openTicket !== null,
  });
  const verification = useQuery({
    queryKey: ['lode', 'verify', verifyDraw],
    queryFn: () => lodeApi.verify(verifyDraw!),
    enabled: verifyDraw !== null,
  });

  const meta = LODE_BET_TYPES[betType];
  const availableBetTypes = useMemo(() => betTypesForRegion(region), [region]);

  /** Dai cua mien dang xem co ky dang mo ban. */
  const openStations: LodeStation[] = useMemo(
    () => (stations.data?.stations ?? []).filter((s) => s.region === region && s.open_draw !== null),
    [stations.data, region],
  );

  /** Ky sieu toc cung mien: bang ty le dang hien la cua mien nay. */
  const regionFastDraws = useMemo(
    () => (fastDraws.data?.draws ?? []).filter((d) => d.region_sim === region),
    [fastDraws.data, region],
  );
  const fastDrawIds = useMemo(() => new Set(regionFastDraws.map((d) => d.id)), [regionFastDraws]);
  const openFastDraws = regionFastDraws.filter(
    (d) => d.status === 'OPEN' && new Date(d.closes_at).getTime() > gioHienTai,
  );

  const oddsRow = useMemo(() => {
    // Dang gop tinh tien theo dong NGUYEN TU no tach ra, nen tra ty le cua dong do.
    const atomic = betType === 'BAO_LO' || betType === 'LO_KEP' ? 'LO2'
      : betType === 'DAN_DE' ? 'DE'
      : betType === 'DAU_DUOI' ? 'DAU'
      : betType;
    return odds.data?.odds.find((o) => o.bet_type === atomic) ?? null;
  }, [odds.data, betType]);


  /** Loai cuoc NGUYEN TU ma dang gop nay tach ra - dung de tra ty le dung o. */
  const atomicBetType = useMemo(() => (
    betType === 'BAO_LO' || betType === 'LO_KEP' ? 'LO2'
      : betType === 'DAN_DE' ? 'DE'
        : betType === 'DAU_DUOI' ? 'DAU'
          : betType
  ), [betType]);

  /**
   * Ty le theo tung ky. Dai co muc rieng thi lay muc do; ky sieu toc va dai chua dat rieng
   * roi ve muc mien (`oddsRow`).
   */
  const oddsByDraw = useMemo(() => {
    const byDraw = new Map<string, string>();
    for (const st of stations.data?.stations ?? []) {
      if (st.open_draw) byDraw.set(st.open_draw.id, st.code);
    }
    const map = new Map<string, { pointPriceMinor: bigint; payoutPerPointMinor: bigint }>();
    for (const drawId of selectedDraws) {
      const code = byDraw.get(drawId);
      const row = code
        ? odds.data?.by_station?.[code]?.find((o) => o.bet_type === atomicBetType) ?? oddsRow
        : oddsRow;
      if (!row) continue;
      map.set(drawId, {
        pointPriceMinor: BigInt(row.point_price.amount_minor),
        payoutPerPointMinor: BigInt(row.payout_per_point.amount_minor),
      });
    }
    return map;
  }, [stations.data, selectedDraws, odds.data, atomicBetType, oddsRow]);

  /** Dai da chon ma o nay chua co bang ty le - neu ten ra de nguoi choi biet bo dai nao. */
  const stationsThieuTyLe = useMemo(() => {
    const out: string[] = [];
    for (const st of stations.data?.stations ?? []) {
      if (!st.open_draw || !selectedDraws.includes(st.open_draw.id)) continue;
      if (!oddsByDraw.has(st.open_draw.id)) out.push(st.name);
    }
    return out;
  }, [stations.data, selectedDraws, oddsByDraw]);

  /**
   * Tien trung cua mot hang. Cac dai da chon lech ty le thi hien KHOANG thap - cao,
   * vi mot con so duy nhat se noi sai ve so tien that nhan duoc o tung dai.
   */
  const payoutText = (calc: { payoutMinor: bigint | null; payoutMaxMinor: bigint | null } | undefined): string => {
    if (!calc || calc.payoutMinor === null) return '—';
    const min = formatMoney({ minor: calc.payoutMinor, currency });
    if (calc.payoutMaxMinor === null || calc.payoutMaxMinor === calc.payoutMinor) return min;
    return `${min} – ${formatMoney({ minor: calc.payoutMaxMinor, currency })}`;
  };

  /** Luoi so: moi hang mot so (hoac mot bo so) voi muc diem rieng. */
  const slip = useMemo(() => calcSlip(
    betType,
    rows,
    selectedDraws,
    oddsRow
      ? {
        pointPriceMinor: BigInt(oddsRow.point_price.amount_minor),
        payoutPerPointMinor: BigInt(oddsRow.payout_per_point.amount_minor),
      }
      : null,
    oddsByDraw,
  ), [betType, rows, selectedDraws, oddsRow, oddsByDraw]);
  const currency = oddsRow?.point_price.currency ?? 'VND';
  const lines = slip.lines;
  const atomicLineCount = slip.atomicLines;
  const rowCalcByKey = useMemo(() => new Map(slip.rows.map((r) => [r.key, r])), [slip.rows]);

  const validation = useMemo(() => {
    if (!isAuthenticated) return 'Đăng nhập để đặt cược.';
    if (!meta) return 'Chọn loại cược.';
    if (slip.error) return slip.error;
    if (selectedDraws.length === 0) return 'Chưa chọn đài. Bấm chọn một hoặc nhiều đài ở mục «Đài».';
    if (meta.multiDraw) {
      if (selectedDraws.length < BAO_LO_MIN_DRAWS) return `Bao lô phải chọn ít nhất ${BAO_LO_MIN_DRAWS} đài.`;
      if (selectedDraws.length > BAO_LO_MAX_DRAWS) return `Bao lô tối đa ${BAO_LO_MAX_DRAWS} đài.`;
    }
    if (lines.length > MAX_INPUT_LINES || atomicLineCount > MAX_ATOMIC_LINES) {
      return `Một phiếu tối đa ${MAX_ATOMIC_LINES} dòng tính tiền; bớt số hoặc bớt đài rồi đặt làm nhiều phiếu.`;
    }
    if (!oddsRow) return 'Ô này chưa có bảng tỷ lệ hiệu lực.';
    if (stationsThieuTyLe.length > 0) {
      return `Chưa có bảng tỷ lệ cho ${stationsThieuTyLe.join(', ')}. Bỏ đài đó hoặc chọn loại cược khác.`;
    }
    return null;
  }, [
    isAuthenticated, meta, slip.error, selectedDraws.length, lines.length, atomicLineCount, oddsRow,
    stationsThieuTyLe,
  ]);

  /** Ten dai dang chon, de buoc xac nhan ghi ro danh tren dai nao. */
  function drawNameOf(drawId: string): string {
    const station = (stations.data?.stations ?? []).find((s) => s.open_draw?.id === drawId);
    if (station) return station.name;
    const fast = (fastDraws.data?.draws ?? []).find((d) => d.id === drawId);
    return fast ? `Siêu tốc ${fast.region_sim}` : drawId;
  }

  /** Gio chot som nhat trong cac dai da chon - qua moc do thi xac nhan cung bi tu choi. */
  function earliestClose(drawIds: readonly string[]): number {
    const closes = drawIds.map((id) => {
      const station = (stations.data?.stations ?? []).find((s) => s.open_draw?.id === id);
      const iso = station?.open_draw?.closes_at
        ?? (fastDraws.data?.draws ?? []).find((d) => d.id === id)?.closes_at;
      return iso ? new Date(iso).getTime() : Number.POSITIVE_INFINITY;
    });
    return Math.min(...closes);
  }

  function openConfirm(): void {
    if (validation !== null) return;
    const digits = meta?.digits ?? 2;
    const pad = (n: number): string => String(n).padStart(digits, '0');
    setConfirmExpired(false);
    setConfirm({
      lines,
      rows: slip.rows.map((r) => ({
        key: r.key,
        label: betType === 'LO_KEP' ? '00, 11 … 99' : r.numbers.map(pad).join(', '),
        points: String(r.pointsX2! / 2).replace('.', ','),
        stake: formatMoney({ minor: r.stakeMinor ?? 0n, currency }),
        payout: payoutText(r),
      })),
      drawNames: selectedDraws.map(drawNameOf),
      betLabel: meta?.label ?? betType,
      atomicLines: atomicLineCount,
      stake: formatMoney({ minor: slip.stakeMinor, currency }),
      expiresAt: Math.min(Date.now() + CONFIRM_WINDOW_MS, earliestClose(selectedDraws)),
      idempotencyKey: crypto.randomUUID(),
    });
  }

  /** Het hai phut (hoac toi gio chot) ma chua xac nhan: tra ve luoi, phieu chua dat. */
  useEffect(() => {
    if (confirm && !buy.isPending && gioHienTai >= confirm.expiresAt) {
      setConfirm(null);
      setConfirmExpired(true);
    }
  });

  function updateRow(key: string, patch: Partial<SlipRow>): void {
    setLastBought(null);
    setConfirmExpired(false);
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addRow(): void {
    setLastBought(null);
    setRows((current) => [...current, newRow(current[current.length - 1]?.points ?? '1')]);
  }

  function removeRow(key: string): void {
    setRows((current) => (current.length <= 1 ? [newRow(current[0]?.points ?? '1')] : current.filter((r) => r.key !== key)));
  }

  const buy = useMutation({
    mutationFn: (pending: { lines: LodeLineInput[]; key: string; atomicLines: number }) =>
      lodeApi.buy(pending.lines, pending.key),
    onSuccess: (created, pending) => {
      setConfirm(null);
      // Giu nguyen dai, loai cuoc va muc diem: nguoi choi thuong danh tiep vai so tren cung dai.
      setRows((current) => [newRow(current[current.length - 1]?.points ?? '1')]);
      setLastBought({ lines: pending.atomicLines, stake: formatMoney(created.stake) });
      void queryClient.invalidateQueries({ queryKey: ['lode', 'tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
    },
  });

  /**
   * Dai truyen thong chon duoc nhieu dai cung luc (moi dai tinh tien rieng). Ky sieu toc
   * thuoc goi san pham khac nen khong gop chung phieu voi dai truyen thong: chon ky sieu
   * toc thi bo cac dai dang chon, va nguoc lai.
   */
  function toggleDraw(drawId: string): void {
    setLastBought(null);
    // Doi dai trong luc xac nhan thi phieu dang xem khong con dung: quay ve luoi.
    setConfirm(null);
    setSelectedDraws((current) => {
      if (fastDrawIds.has(drawId)) return current[0] === drawId ? [] : [drawId];
      const stationOnly = current.filter((d) => !fastDrawIds.has(d));
      return stationOnly.includes(drawId) ? stationOnly.filter((d) => d !== drawId) : [...stationOnly, drawId];
    });
  }

  function switchRegion(next: LodeRegion): void {
    setConfirm(null);
    setRegion(next);
    setSelectedDraws([]);
    const allowed = betTypesForRegion(next);
    if (!allowed.includes(betType)) setBetType(allowed[0] ?? 'DE');
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Lô đề ba miền</h1>
        <nav className={styles.tabs}>
          <button
            type="button"
            className={tab === 'dat-cuoc' ? styles.tabActive : styles.tab}
            onClick={() => setTab('dat-cuoc')}
          >
            Đặt cược
          </button>
          <button
            type="button"
            className={tab === 've-cua-toi' ? styles.tabActive : styles.tab}
            onClick={() => setTab('ve-cua-toi')}
          >
            Vé của tôi
          </button>
        </nav>
      </header>

      {tab === 'dat-cuoc' ? (
        <div className={styles.layout}>
          <div className={styles.main}>
            <Card title="Miền và kỳ quay">
              <div className={styles.regions}>
                {REGIONS.map((r) => (
                  <button
                    key={r.code}
                    type="button"
                    className={region === r.code ? styles.regionActive : styles.region}
                    onClick={() => switchRegion(r.code)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>

              {stations.isLoading ? (
                <Skeleton height={72} />
              ) : openStations.length === 0 ? (
                <EmptyState
                  title="Chưa có kỳ nào mở bán"
                  description="Nhà khai thác chưa bật đài của miền này, hoặc kỳ hôm nay đã chốt giờ."
                />
              ) : (
                <ul className={styles.drawList}>
                  {openStations.map((station) => {
                    const draw = station.open_draw!;
                    const picked = selectedDraws.includes(draw.id);
                    return (
                      <li key={station.code}>
                        <button
                          type="button"
                          className={picked ? styles.drawCardActive : styles.drawCard}
                          onClick={() => toggleDraw(draw.id)}
                          aria-pressed={picked}
                        >
                          <span className={styles.drawName}>{station.name}</span>
                          <span className={styles.drawMeta}>
                            Chốt cược {gioVn(draw.closes_at)} · Quay {gioVn(draw.draw_time)}
                          </span>
                          <span className={styles.drawMeta}>
                            {nhanGioChot(draw.closes_at, gioHienTai)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card
              title="Kỳ siêu tốc"
              action={<Badge tone="brand">Tự động quay</Badge>}
            >
              <p className={styles.note}>
                Kỳ siêu tốc quay theo chu kỳ ngắn và dùng sinh số cam kết trước — mã băm
                công bố trước khi mở bán, hạt giống chỉ lộ ra sau khi chốt. Mở trang xác
                minh của kỳ để tự băm lại SHA-256 và đối chiếu.
              </p>
              {fastDraws.isLoading ? (
                <Skeleton height={56} />
              ) : regionFastDraws.length === 0 ? (
                <EmptyState title="Chưa có kỳ siêu tốc nào đang mở" />
              ) : (
                <ul className={styles.drawList}>
                  {regionFastDraws.map((draw) => {
                    const picked = selectedDraws.includes(draw.id);
                    // Kỳ đã xếp hàng nhưng chưa tới giờ: hiện để đếm ngược, không bấm được.
                    const chuaMo = draw.status === 'SCHEDULED';
                    /**
                     * Kỳ đã qua giờ chốt cũng không đặt được nữa, KỂ CẢ khi trạng thái
                     * còn là `OPEN`: tác vụ nền chỉ chạy mười giây một lần nên có vài
                     * giây kỳ đã hết giờ mà nhãn trạng thái chưa kịp đổi. Máy chủ kiểm
                     * `closes_at` độc lập với trạng thái và sẽ từ chối; chặn ở đây để
                     * người chơi không bấm vào một thẻ chắc chắn báo lỗi.
                     */
                    const daChot = new Date(draw.closes_at).getTime() <= gioHienTai;
                    const khongDatDuoc = chuaMo || daChot;
                    return (
                      <li key={draw.id}>
                        <button
                          type="button"
                          className={picked ? styles.drawCardActive : styles.drawCard}
                          onClick={() => toggleDraw(draw.id)}
                          aria-pressed={picked}
                          disabled={khongDatDuoc}
                        >
                          <span className={styles.drawName}>Siêu tốc {draw.region_sim}</span>
                          <span className={styles.drawMeta}>
                            {chuaMo
                              ? nhanGioMo(draw.opens_at, gioHienTai)
                              : nhanGioChot(draw.closes_at, gioHienTai)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card title="Bảng tỷ lệ đang hiệu lực">
              {odds.isLoading ? (
                <Skeleton height={120} />
              ) : (odds.data?.odds ?? []).length === 0 ? (
                <EmptyState
                  title="Chưa có bảng tỷ lệ"
                  description="Nhà khai thác chưa ghi bảng tỷ lệ cho miền này nên chưa mời cược."
                />
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col">Loại cược</th>
                      <th scope="col">Giá một điểm</th>
                      <th scope="col">Trả mỗi điểm</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(odds.data?.odds ?? []).map((row) => (
                      <tr key={row.bet_type}>
                        <td>
                          {LODE_BET_TYPES[row.bet_type]?.label ?? row.bet_type}
                          {row.custom && <> <Badge tone="brand">Giá riêng</Badge></>}
                        </td>
                        <td className={styles.num}>{formatMoney(row.point_price)}</td>
                        <td className={styles.num}>{formatMoney(row.payout_per_point)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          <aside className={styles.slip}>
              <Card title={confirm ? 'Xác nhận phiếu cược' : 'Phiếu cược'}>
              {confirm ? (
                <>
                  <p className={styles.countdown} role="timer">
                    Còn <strong>{nhanConLai(confirm.expiresAt, gioHienTai)}</strong> để xác nhận. Chưa xác nhận thì
                    chưa trừ tiền; muốn đổi số hoặc đổi tiền bấm «Sửa phiếu».
                  </p>
                  <p className={styles.hint}>
                    {confirm.betLabel} · {confirm.drawNames.join(', ')}
                  </p>
                  <table className={styles.grid}>
                    <thead>
                      <tr>
                        <th scope="col">Số</th>
                        <th scope="col" className={styles.num}>Điểm · tiền</th>
                        <th scope="col" className={styles.num}>Tiền trúng</th>
                      </tr>
                    </thead>
                    <tbody>
                      {confirm.rows.map((r) => (
                        <tr key={r.key}>
                          <td className={styles.gridNumber}>{r.label}</td>
                          <td className={styles.num}>
                            {r.points} điểm
                            <span className={styles.gridSub}>{r.stake}</span>
                          </td>
                          <td className={styles.num}>{r.payout}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <dl className={styles.summary}>
                    <div>
                      <dt>Số dòng tính tiền</dt>
                      <dd>{confirm.atomicLines}</dd>
                    </div>
                    <div>
                      <dt>Tổng tiền cược</dt>
                      <dd>{confirm.stake}</dd>
                    </div>
                  </dl>
                  {buy.error instanceof ApiError && <ErrorList messages={buy.error.messages} />}
                  <div className={styles.confirmActions}>
                    <Button
                      variant="ghost"
                      disabled={buy.isPending}
                      onClick={() => {
                        buy.reset();
                        setConfirm(null);
                      }}
                    >
                      Sửa phiếu
                    </Button>
                    <Button
                      loading={buy.isPending}
                      disabled={buy.isPending}
                      onClick={() => buy.mutate({
                        lines: confirm.lines, key: confirm.idempotencyKey, atomicLines: confirm.atomicLines,
                      })}
                    >
                      Xác nhận đặt cược
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <label className={styles.label} htmlFor="lode-bet-type">Loại cược</label>
                  <select
                    id="lode-bet-type"
                    className={styles.select}
                    value={betType}
                    onChange={(e) => {
                      setBetType(e.target.value);
                      setLastBought(null);
                      // Lo kep khong nhap so: mot hang duy nhat, giu muc diem dang go.
                      if (e.target.value === 'LO_KEP') setRows((current) => [newRow(current[0]?.points ?? '1')]);
                    }}
                  >
                    {availableBetTypes.map((code) => (
                      <option key={code} value={code}>
                        {LODE_BET_TYPES[code]?.label ?? code}
                      </option>
                    ))}
                  </select>
                  {meta?.hint && <p className={styles.hint}>{meta.hint}</p>}
                  {meta?.expandsTo && (
                    <p className={styles.hint}>Tách thành {meta.expandsTo}, mỗi dòng tính tiền riêng.</p>
                  )}
  
                  <p className={styles.label}>Đài</p>
                  {openStations.length === 0 && openFastDraws.length === 0 ? (
                    <p className={styles.hint}>Miền này hiện chưa có đài nào mở bán.</p>
                  ) : (
                    <div className={styles.chips}>
                      {openStations.map((station) => {
                        const drawId = station.open_draw!.id;
                        const picked = selectedDraws.includes(drawId);
                        return (
                          <button
                            key={station.code}
                            type="button"
                            className={picked ? styles.chipActive : styles.chip}
                            aria-pressed={picked}
                            onClick={() => toggleDraw(drawId)}
                          >
                            {picked ? '✓ ' : ''}{station.name} · chốt {gioVn(station.open_draw!.closes_at)}
                          </button>
                        );
                      })}
                      {openFastDraws.map((draw) => {
                        const picked = selectedDraws.includes(draw.id);
                        return (
                          <button
                            key={draw.id}
                            type="button"
                            className={picked ? styles.chipActive : styles.chip}
                            aria-pressed={picked}
                            onClick={() => toggleDraw(draw.id)}
                          >
                            {picked ? '✓ ' : ''}Siêu tốc
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className={styles.hint}>
                    {meta?.multiDraw
                      ? 'Bao lô: chọn từ 2 đài trở lên.'
                      : 'Chọn được nhiều đài cùng lúc; mỗi đài tính tiền riêng.'}
                  </p>

                  <p className={styles.label}>
                    {meta?.picks === 1
                      ? `Số (${meta.digits} chữ số), điểm và tiền trúng từng số`
                      : betType === 'LO_KEP'
                        ? 'Điểm cho cả mười số kép'
                        : meta?.picks === -1
                          ? 'Mỗi hàng một dàn (các số cách nhau dấu phẩy)'
                          : `Mỗi hàng một bộ ${meta?.picks ?? 2} số (cách nhau dấu phẩy)`}
                  </p>
                  <table className={styles.grid}>
                    <thead>
                      <tr>
                        <th scope="col">Số</th>
                        <th scope="col">Điểm</th>
                        <th scope="col" className={styles.num}>
                          {meta?.perHit ? 'Trúng / nháy' : 'Tiền trúng'}
                        </th>
                        <th scope="col"><span className={styles.srOnly}>Xóa hàng</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const calc = rowCalcByKey.get(row.key);
                        return (
                          <tr key={row.key} className={calc?.error ? styles.gridRowError : undefined}>
                            <td>
                              {betType === 'LO_KEP' ? (
                                <span className={styles.gridNumber}>00, 11 … 99</span>
                              ) : (
                                <input
                                  className={styles.gridInput}
                                  inputMode="decimal"
                                  autoComplete="off"
                                  aria-label="Số"
                                  placeholder={meta?.digits === 3 ? '123' : meta?.digits === 4 ? '1234' : '12'}
                                  value={row.numbersRaw}
                                  onChange={(e) => updateRow(row.key, { numbersRaw: e.target.value })}
                                  onBlur={() => setRows((current) => expandRows(current, betType))}
                                />
                              )}
                            </td>
                            <td>
                              <input
                                className={styles.gridInput}
                                inputMode="decimal"
                                autoComplete="off"
                                aria-label="Điểm"
                                value={row.points}
                                onChange={(e) => updateRow(row.key, { points: e.target.value })}
                              />
                              {calc?.stakeMinor != null && selectedDraws.length > 0 && (
                                <span className={styles.gridSub}>
                                  {formatMoney({ minor: calc.stakeMinor, currency })}
                                </span>
                              )}
                            </td>
                            <td className={styles.num}>
                              {payoutText(calc)}
                            </td>
                            <td>
                              <button
                                type="button"
                                className={styles.gridRemove}
                                aria-label="Xóa hàng"
                                onClick={() => removeRow(row.key)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {betType !== 'LO_KEP' && (
                    <button type="button" className={styles.addRow} onClick={addRow}>
                      + Thêm số
                    </button>
                  )}
                  <p className={styles.hint}>
                    Điểm áp cho mỗi đài; tiền cược ghi dưới ô điểm đã nhân số đài.
                    {meta?.picks === 1 && ` Gõ nhiều số vào một ô (${meta.digits === 2 ? '11,51,80 hoặc 115180' : '123,456'}) sẽ tự tách thành từng hàng.`}
                    {meta?.perHit && ' Lô trả theo số nháy: số về mấy lần thì nhân lên bấy nhiêu lần.'}
                  </p>

                  <dl className={styles.summary}>
                    <div>
                      <dt>Số dòng tính tiền</dt>
                      <dd>{atomicLineCount}</dd>
                    </div>
                    <div>
                      <dt>Tổng tiền cược</dt>
                      <dd>{oddsRow ? formatMoney({ minor: slip.stakeMinor, currency }) : '—'}</dd>
                    </div>
                  </dl>

                  {lastBought && (
                    <div className={styles.success}>
                      Đã đặt cược {lastBought.lines} dòng, tổng {lastBought.stake}. Nhập số mới để đánh tiếp.
                      <button type="button" className={styles.linkButton} onClick={() => setTab('ve-cua-toi')}>
                        Xem vé
                      </button>
                    </div>
                  )}
                  {confirmExpired && (
                    <p className={styles.validation}>
                      Quá thời gian xác nhận nên phiếu chưa được đặt, chưa trừ tiền. Kiểm lại rồi bấm «Đặt cược».
                    </p>
                  )}
                  {!lastBought && validation && <p className={styles.validation}>{validation}</p>}

                  <Button
                    fullWidth
                    disabled={validation !== null}
                    onClick={openConfirm}
                  >
                    Đặt cược
                  </Button>
                  <p className={styles.hint}>Bấm «Đặt cược» để xem lại phiếu; có 2 phút để xác nhận hoặc sửa.</p>
                </>
              )}
            </Card>
          </aside>
        </div>
      ) : (
        <Card title="Vé lô đề của tôi">
          {tickets.isLoading ? (
            <Skeleton height={120} />
          ) : (tickets.data?.tickets ?? []).length === 0 ? (
            <EmptyState title="Chưa có vé nào" description="Vé mua xong sẽ hiện ở đây." />
          ) : (
            <ul className={styles.ticketList}>
              {(tickets.data?.tickets ?? []).map((t) => {
                const status = TICKET_STATUS[t.status] ?? { label: t.status, tone: 'neutral' as const };
                return (
                  <li key={t.id} className={styles.ticket}>
                    <button
                      type="button"
                      className={styles.ticketHead}
                      onClick={() => setOpenTicket(openTicket === t.id ? null : t.id)}
                      aria-expanded={openTicket === t.id}
                    >
                      <span className={styles.ticketTime}>
                        {new Date(t.server_time).toLocaleString('vi-VN')}
                      </span>
                      <span className={styles.num}>{formatMoney(t.stake)}</span>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </button>

                    {openTicket === t.id && (
                      <div className={styles.ticketBody}>
                        {ticketDetail.isLoading ? (
                          <Skeleton height={80} />
                        ) : (
                          <table className={styles.table}>
                            <thead>
                              <tr>
                                <th scope="col">Loại</th>
                                <th scope="col">Số</th>
                                <th scope="col">Điểm</th>
                                <th scope="col">Tỷ lệ đã chụp</th>
                                <th scope="col">Nháy</th>
                                <th scope="col">Tiền trả</th>
                                <th scope="col">Trạng thái</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(ticketDetail.data?.lines ?? []).map((line) => {
                                const ls = LINE_STATUS[line.status]
                                  ?? { label: line.status, tone: 'neutral' as const };
                                return (
                                  <tr key={line.id}>
                                    <td>{LODE_BET_TYPES[line.bet_type]?.label ?? line.bet_type}</td>
                                    <td className={styles.num}>{line.numbers.join(', ')}</td>
                                    <td className={styles.num}>{line.points_x2 / 2}</td>
                                    <td className={styles.num}>
                                      {formatMoney(line.odds_snapshot.payout_per_point)}
                                    </td>
                                    <td className={styles.num}>{line.hits ?? '—'}</td>
                                    <td className={styles.num}>
                                      {line.payout ? formatMoney(line.payout) : '—'}
                                    </td>
                                    <td>
                                      <Badge tone={ls.tone}>{ls.label}</Badge>
                                      <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => setVerifyDraw(line.draw_id)}
                                      >
                                        Xác minh kỳ
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {verifyDraw && (
            <div className={styles.verify}>
              <div className={styles.verifyHead}>
                <h2 className={styles.verifyTitle}>Xác minh kỳ {verifyDraw}</h2>
                <Button variant="ghost" size="sm" onClick={() => setVerifyDraw(null)}>Đóng</Button>
              </div>
              {verification.isLoading ? (
                <Skeleton height={100} />
              ) : !verification.data?.commitment ? (
                <p className={styles.note}>
                  Kỳ này lấy kết quả từ đài quay thật, không có cam kết sinh số để đối chiếu.
                  Mã kết quả: <code>{verification.data?.result_hash ?? '—'}</code>
                </p>
              ) : (
                <dl className={styles.verifyGrid}>
                  <div>
                    <dt>Mã băm công bố trước</dt>
                    <dd><code>{verification.data.commitment.server_seed_hash}</code></dd>
                  </div>
                  <div>
                    <dt>Công bố lúc</dt>
                    <dd>{new Date(verification.data.commitment.published_at).toLocaleString('vi-VN')}</dd>
                  </div>
                  <div>
                    <dt>Hạt giống đã tiết lộ</dt>
                    <dd><code>{verification.data.commitment.revealed_seed ?? 'chưa tiết lộ'}</code></dd>
                  </div>
                  <div>
                    <dt>Bảng kết quả</dt>
                    <dd>{verification.data.prizes?.join(', ') ?? '—'}</dd>
                  </div>
                </dl>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
